import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IFSNode, SlideProps } from './types';
import {
  Brain,
  Zap,
  Database,
  MessageSquare,
  Layers,
  FileText,
  Repeat,
  Code,
  Sparkles,
  User,
} from 'lucide-react';

// --- Shared Components ---

const Label = ({ children, className = '' }: { children?: React.ReactNode; className?: string }) => (
  <span className={`text-[10px] uppercase tracking-[0.2em] text-muted font-medium ${className}`}>{children}</span>
);

// --- Generic Geometric Abstraction (For Philosophical Slides) ---
// Uses the "Early Avant-Garde" aesthetic from the prompt

export const GeometricAbstraction: React.FC<SlideProps> = ({ variant = 'circle' }) => {
  return (
    <div className="w-full h-[400px] flex items-center justify-center overflow-hidden relative">
      <div
        className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(#333 1px, transparent 1px)', backgroundSize: '20px 20px' }}
      />

      {variant === 'circle' && (
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative">
          <div className="w-64 h-64 rounded-full border border-gold/30 flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              className="w-48 h-48 border border-dashed border-muted rounded-full"
            />
            <div className="absolute w-24 h-24 bg-gold/10 rounded-full blur-xl" />
          </div>
        </motion.div>
      )}

      {variant === 'triangle' && (
        <svg viewBox="0 0 200 200" className="w-64 h-64 overflow-visible">
          <motion.path
            d="M100,20 L180,180 L20,180 Z"
            fill="none"
            stroke="#d4a373"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2 }}
          />
          <motion.circle
            cx="100"
            cy="80"
            r="20"
            fill="#1a1a1a"
            stroke="#e8e8e8"
            strokeWidth="0.5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          />
        </svg>
      )}

      {variant === 'chaos' && (
        <div className="relative w-64 h-64">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute top-1/2 left-1/2 w-32 h-[1px] bg-gold"
              animate={{ rotate: [0, 180, 360], scale: [1, 1.2, 1] }}
              transition={{ duration: 10 + i, repeat: Infinity, ease: 'linear' }}
              style={{ marginLeft: '-64px' }}
            />
          ))}
          <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gold">
            <Sparkles size={32} />
          </motion.div>
        </div>
      )}

      {variant === 'dual' && (
        <div className="flex gap-8 items-center">
          <motion.div
            className="w-24 h-24 border border-muted rounded-full"
            animate={{ x: [0, 10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <div className="h-32 w-[1px] bg-gold/50" />
          <motion.div
            className="w-24 h-24 bg-gold/10 border border-gold rounded-full"
            animate={{ x: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
        </div>
      )}
    </div>
  );
};

// --- Metaphor: Context Window (The 15MB File) ---

export const ContextVisual: React.FC<SlideProps> = () => {
  return (
    <div className="relative w-full h-[400px] flex items-center justify-center">
      {/* Central "About Me" File */}
      <motion.div
        className="w-32 h-40 bg-[#151515] border border-gold flex flex-col items-center justify-center z-10 shadow-[0_0_30px_rgba(212,163,115,0.15)]"
        initial={{ scale: 0.9 }}
        animate={{ scale: [0.9, 1, 0.9] }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        <FileText className="text-gold mb-2" size={24} />
        <div className="text-[8px] text-gold tracking-widest uppercase">About_Me.md</div>
        <div className="text-[8px] text-muted mt-1">15 MB Context</div>
      </motion.div>

      {/* Radiating Connections */}
      {[0, 60, 120, 180, 240, 300].map((deg, i) => (
        <motion.div
          key={i}
          className="absolute w-[200px] h-[1px] bg-gradient-to-r from-gold/50 to-transparent origin-left"
          style={{ rotate: deg, left: '50%' }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.2 + i * 0.1, duration: 1 }}
        >
          <motion.div
            className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-muted rounded-full"
            animate={{ opacity: [0.2, 1, 0.2] }}
            transition={{ duration: 2, repeat: Infinity, delay: i * 0.5 }}
          />
        </motion.div>
      ))}

      <div className="absolute bottom-8 text-center text-xs text-muted">"I don't prompt from scratch. I bring my own memory."</div>
    </div>
  );
};

// --- Metaphor: Ecosystem (5 Steps) ---

export const EcosystemVisual: React.FC<SlideProps> = () => {
  const steps = [
    { id: 1, label: 'Contact', icon: <MessageSquare size={14} /> },
    { id: 2, label: 'Pre-Work', icon: <Database size={14} /> },
    { id: 3, label: 'Session', icon: <User size={14} />, active: true },
    { id: 4, label: 'Post', icon: <Zap size={14} /> },
    { id: 5, label: 'Re-Engage', icon: <Repeat size={14} /> },
  ];

  return (
    <div className="w-full flex flex-col items-center justify-center gap-8 h-[300px]">
      <div className="flex items-center gap-4 relative">
        {/* Connection Line */}
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-border -z-10" />

        {steps.map((step, idx) => (
          <React.Fragment key={step.id}>
            <motion.div
              className={`w-12 h-12 rounded border flex items-center justify-center bg-bg relative z-10
                ${step.active ? 'border-gold text-gold shadow-lg shadow-gold/20' : 'border-border text-muted'}
              `}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              {step.icon}
              <div className="absolute -bottom-6 whitespace-nowrap text-[9px] uppercase tracking-wider font-medium text-muted">
                {step.label}
              </div>
            </motion.div>
            {idx < steps.length - 1 && <div className="w-8" />}
          </React.Fragment>
        ))}
      </div>

      <motion.div
        className="mt-8 p-3 border border-dashed border-gold/50 rounded bg-gold/5 text-xs text-gold font-mono"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        Real-time Markers (Granola) → Zapier → Knowledge Base
      </motion.div>
    </div>
  );
};

// --- Metaphor: Dogfooding (Circle) ---

export const DogfoodingVisual: React.FC<SlideProps> = () => {
  return (
    <div className="relative w-full h-[400px] flex items-center justify-center">
      <svg viewBox="0 0 200 200" className="w-64 h-64 overflow-visible animate-[spin_20s_linear_infinite]">
        <defs>
          <path id="circlePath" d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" />
        </defs>
        <circle cx="100" cy="100" r="75" fill="none" stroke="#333" strokeWidth="1" strokeDasharray="4 4" />

        <text fill="#d4a373" fontSize="10" letterSpacing="4" fontWeight="bold">
          <textPath href="#circlePath" startOffset="0%">
            BUILD FOR SELF • TEST IN PRACTICE • SHIP PRODUCT •
          </textPath>
        </text>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-1">🛠️</div>
          <div className="text-[10px] uppercase text-muted tracking-widest">Feedback Loop</div>
        </div>
      </div>
    </div>
  );
};

// --- Metaphor: Vibe Coding ---

export const VibeCodingVisual: React.FC<SlideProps> = () => {
  return (
    <div className="w-full max-w-2xl mx-auto grid grid-cols-3 gap-4 items-center h-[300px]">
      <div className="text-center opacity-30">
        <Code className="mx-auto mb-2" />
        <div className="text-[10px] uppercase">Traditional</div>
        <div className="text-xs font-mono mt-1">
          Syntax
          <br />
          Logic
          <br />
          Compilers
        </div>
      </div>

      <div className="relative flex flex-col items-center">
        <div className="w-full h-[1px] bg-gold absolute top-1/2 -z-10" />
        <motion.div
          className="w-16 h-16 bg-bg border border-gold rounded-full flex items-center justify-center z-10"
          animate={{
            boxShadow: [
              '0 0 0px rgba(212,163,115,0)',
              '0 0 20px rgba(212,163,115,0.5)',
              '0 0 0px rgba(212,163,115,0)',
            ],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Sparkles size={20} className="text-gold" />
        </motion.div>
        <div className="mt-4 text-center">
          <div className="text-[10px] uppercase text-gold font-bold">Vibe Coding</div>
          <div className="text-xs text-text mt-1">"Make it beautiful"</div>
        </div>
      </div>

      <div className="text-center opacity-30">
        <div className="text-4xl mb-2 grayscale">🧠</div>
        <div className="text-[10px] uppercase">Software 3.0</div>
        <div className="text-xs font-mono mt-1">
          Neural
          <br />
          Probabilistic
          <br />
          Fuzzy
        </div>
      </div>
    </div>
  );
};

// --- Metaphor: Agency Scale ---

export const AgencyVisual: React.FC<SlideProps> = ({ isActive }) => {
  const [level, setLevel] = useState(1);

  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      setLevel((prev) => (prev === 3 ? 1 : prev + 1));
    }, 2000);
    return () => clearInterval(interval);
  }, [isActive]);

  return (
    <div className="w-full max-w-lg mx-auto h-[300px] flex flex-col justify-center">
      <div className="flex justify-between items-end h-32 mb-4 border-b border-border pb-2">
        <div
          className={`w-1/3 bg-muted/20 mx-1 transition-all duration-500 rounded-t ${
            level >= 1 ? 'h-1/3 bg-muted' : 'h-1/6'
          }`}
        ></div>
        <div
          className={`w-1/3 bg-gold/20 mx-1 transition-all duration-500 rounded-t ${
            level >= 2 ? 'h-2/3 bg-gold' : 'h-1/6'
          }`}
        ></div>
        <div
          className={`w-1/3 bg-red/20 mx-1 transition-all duration-500 rounded-t ${
            level >= 3 ? 'h-full bg-red' : 'h-1/6'
          }`}
        ></div>
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted text-center">
        <div className="w-1/3">
          Passive
          <br />
          (Wait)
        </div>
        <div className="w-1/3 text-gold">
          Augmented
          <br />
          (Nudge)
        </div>
        <div className="w-1/3">
          Autonomous
          <br />
          (Interrupt)
        </div>
      </div>
      <div className="mt-8 text-center text-sm font-serif italic text-text">
        {level === 1 && "\"I'm here if you need me.\""}
        {level === 2 && '"Based on your context, look at this."'}
        {level === 3 && '"Stop. You are making a mistake."'}
      </div>
    </div>
  );
};

// --- Re-exporting Original Visuals ---

export const ExoskeletonVisual: React.FC<SlideProps> = ({ isActive }) => {
  const [activePoint, setActivePoint] = useState<string | null>(null);
  const [mode, setMode] = useState<'heavy' | 'light'>('light');

  const points = [
    { id: 'memory', x: 50, y: 30, label: 'MEMORY', desc: 'Context Retention' },
    { id: 'analysis', x: 75, y: 45, label: 'ANALYSIS', desc: 'Pattern Recognition' },
    { id: 'synthesis', x: 25, y: 45, label: 'SYNTHESIS', desc: 'Real-time Summary' },
  ];

  return (
    <div className="relative w-full h-[400px] flex items-center justify-center">
      {/* Controls */}
      <div className="absolute top-0 right-0 z-20 flex gap-4">
        <button
          onClick={() => setMode('heavy')}
          className={`px-4 py-2 text-xs border transition-colors ${
            mode === 'heavy' ? 'border-gold text-gold' : 'border-border text-muted hover:text-text'
          }`}
        >
          HEAVY ARMOR
        </button>
        <button
          onClick={() => setMode('light')}
          className={`px-4 py-2 text-xs border transition-colors ${
            mode === 'light' ? 'border-gold text-gold' : 'border-border text-muted hover:text-text'
          }`}
        >
          FLUID EXO
        </button>
      </div>

      <div className="relative h-[300px] aspect-[3/4]">
        {/* Human Silhouette (Abstract) */}
        <svg viewBox="0 0 100 150" className="w-full h-full opacity-50 overflow-visible">
          <motion.path
            d="M50,20 C50,10 60,10 60,20 C60,30 40,30 40,20 C40,10 50,10 50,20 M50,30 L50,80 M20,50 L50,35 L80,50 M35,140 L50,80 L65,140"
            fill="none"
            stroke="#e8e8e8"
            strokeWidth="2"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: isActive ? 1 : 0 }}
            transition={{ duration: 2, ease: 'easeInOut' }}
          />
        </svg>

        {/* Exoskeleton Overlay */}
        <svg viewBox="0 0 100 150" className="absolute inset-0 w-full h-full overflow-visible">
          <AnimatePresence mode="wait">
            {mode === 'heavy' ? (
              <motion.path
                key="heavy"
                d="M40,15 L60,15 L70,35 L80,60 L90,60 M30,35 L20,60 L10,60 M40,80 L30,140 L20,140 M60,80 L70,140 L80,140"
                fill="none"
                stroke="#f8ad9d"
                strokeWidth="6"
                strokeLinecap="square"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                exit={{ opacity: 0 }}
              />
            ) : (
              <motion.path
                key="light"
                d="M50,10 L50,5 M30,35 L50,35 L70,35 M50,35 L50,80 M50,80 L35,110 M50,80 L65,110"
                fill="none"
                stroke="#d4a373"
                strokeWidth="1.5"
                strokeDasharray="4 2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            )}
          </AnimatePresence>

          {/* Interactive Points (Only in Light/Optimal mode) */}
          {mode === 'light' &&
            points.map((p) => (
              <g key={p.id} onMouseEnter={() => setActivePoint(p.id)} onMouseLeave={() => setActivePoint(null)}>
                <motion.circle
                  cx={p.x}
                  cy={p.y}
                  r="3"
                  fill="#0a0a0a"
                  stroke="#d4a373"
                  strokeWidth="1"
                  animate={{
                    r: activePoint === p.id ? 5 : 3,
                    strokeWidth: activePoint === p.id ? 2 : 1,
                  }}
                />
                <motion.circle
                  cx={p.x}
                  cy={p.y}
                  r="8"
                  fill="transparent"
                  stroke="#d4a373"
                  strokeWidth="0.5"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </g>
            ))}
        </svg>

        {/* Tooltip */}
        <AnimatePresence>
          {activePoint && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bg-card border border-border p-3 rounded shadow-xl min-w-[150px] pointer-events-none z-30"
              style={{
                top: `${points.find((p) => p.id === activePoint)!.y}%`,
                left: `${points.find((p) => p.id === activePoint)!.x + 10}%`,
              }}
            >
              <div className="text-gold text-xs font-bold mb-1">{points.find((p) => p.id === activePoint)?.label}</div>
              <div className="text-text text-[10px]">{points.find((p) => p.id === activePoint)?.desc}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="absolute bottom-4 text-center">
        <p className="text-sm text-muted max-w-md">
          {mode === 'heavy' ? (
            'Heavy armor restricts movement. It tries to do the work FOR you.'
          ) : (
            <span className="text-gold">Fluid interface enhances natural capability without resistance.</span>
          )}
        </p>
      </div>
    </div>
  );
};

export const PrompterVisual: React.FC<SlideProps> = () => {
  return (
    <div className="relative w-full h-[400px] bg-bg overflow-hidden flex items-center justify-center">
      <motion.div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, rgba(212, 163, 115, 0.15) 0%, rgba(10, 10, 10, 1) 60%)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
      />
      <div className="relative z-10 flex items-center gap-16">
        <motion.div
          className="flex flex-col items-center opacity-40 hover:opacity-100 transition-opacity duration-500"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 0.4 }}
          transition={{ delay: 0.5 }}
        >
          <div className="w-12 h-12 border border-dashed border-muted rounded flex items-center justify-center mb-2">
            <Database size={20} className="text-muted" />
          </div>
          <Label className="text-[9px]">Backend</Label>
        </motion.div>
        <motion.div
          className="h-[1px] w-24 border-t border-dotted border-gold/50"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          style={{ transformOrigin: 'left' }}
          transition={{ delay: 1 }}
        />
        <div className="flex flex-col items-center">
          <div className="relative mb-4">
            <motion.div
              className="absolute inset-0 bg-gold blur-xl opacity-20"
              animate={{ opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <div className="w-20 h-20 rounded-full border border-gold bg-[#151515] flex items-center justify-center relative z-10">
              <span className="text-2xl">👤</span>
            </div>
          </div>
          <h3 className="text-xl font-serif italic text-text">The Coach</h3>
          <p className="text-xs text-muted mt-2">Human connection intact</p>
        </div>
      </div>
      <div className="absolute bottom-8 text-center px-4">
        <Label>Metaphor</Label>
        <p className="mt-2 text-text/80 font-serif italic">"The prompter doesn't walk on stage. They make the actor shine."</p>
      </div>
    </div>
  );
};

export const IFSVisual: React.FC<SlideProps> = () => {
  const [hoveredPart, setHoveredPart] = useState<string | null>(null);

  const parts: IFSNode[] = [
    { id: 'manager', label: 'STRATEGIST', icon: '📐', desc: 'Planning & Logic', angle: 270, type: 'manager' },
    { id: 'critic', label: 'CRITIC', icon: '🛡️', desc: 'Standards', angle: 330, type: 'manager' },
    { id: 'exile', label: 'CHILD', icon: '🔵', desc: 'Vulnerability', angle: 90, type: 'exile' },
    { id: 'firefighter', label: 'REBEL', icon: '⚡', desc: 'Autonomy', angle: 210, type: 'firefighter' },
    { id: 'sage', label: 'SAGE', icon: '📖', desc: 'Meaning', angle: 30, type: 'manager' },
    { id: 'lover', label: 'LOVER', icon: '❤️', desc: 'Connection', angle: 150, type: 'exile' },
  ];

  const radius = 120;

  return (
    <div className="w-full h-[500px] flex items-center justify-center overflow-hidden relative">
      <div className="absolute border border-border rounded-full w-[240px] h-[240px] opacity-30" />
      <div className="absolute border border-border border-dashed rounded-full w-[350px] h-[350px] opacity-20" />

      <div className="z-10 w-24 h-24 rounded-full bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border border-gold flex flex-col items-center justify-center shadow-[0_0_30px_rgba(212,163,115,0.2)]">
        <span className="text-2xl">☀️</span>
        <span className="text-xs font-bold text-gold mt-1 tracking-widest">SELF</span>
      </div>

      {parts.map((part) => {
        const radian = (part.angle * Math.PI) / 180;
        const x = Math.cos(radian) * radius;
        const y = Math.sin(radian) * radius;
        const isHovered = hoveredPart === part.id;

        return (
          <motion.div
            key={part.id}
            className="absolute z-20 cursor-pointer"
            style={{ x, y }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: isHovered ? 1.2 : 1 }}
            transition={{ delay: 0.2 }}
            onMouseEnter={() => setHoveredPart(part.id)}
            onMouseLeave={() => setHoveredPart(null)}
          >
            <svg
              className="absolute top-1/2 left-1/2 w-[300px] h-[300px] pointer-events-none -translate-x-1/2 -translate-y-1/2 overflow-visible"
              style={{ zIndex: -1 }}
            >
              <motion.line
                x1="150"
                y1="150"
                x2={150 - x}
                y2={150 - y}
                stroke={isHovered ? '#d4a373' : '#333'}
                strokeWidth={isHovered ? 2 : 1}
                strokeDasharray={isHovered ? '0' : '4 4'}
              />
            </svg>
            <div
              className={`
              w-12 h-12 rounded-full border flex items-center justify-center bg-bg transition-colors
              ${isHovered ? 'border-gold shadow-[0_0_15px_rgba(212,163,115,0.4)]' : 'border-border'}
            `}
            >
              <span className="text-lg">{part.icon}</span>
            </div>
            <div
              className={`absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap text-[10px] tracking-wider transition-colors ${
                isHovered ? 'text-gold' : 'text-muted'
              }`}
            >
              {part.label}
            </div>
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute top-14 left-1/2 -translate-x-1/2 bg-card border border-border px-3 py-2 rounded min-w-[120px] text-center z-50 pointer-events-none"
                >
                  <div className="text-blue text-[10px] uppercase mb-1">{part.type}</div>
                  <div className="text-text text-xs">{part.desc}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

export const ComplexityScaleVisual: React.FC<SlideProps> = () => {
  const [value, setValue] = useState(50);
  let zone = 'OPTIMAL';
  let color = '#d4a373';
  if (value < 30) {
    zone = 'TOO SIMPLE';
    color = '#7dd3fc';
  } else if (value > 70) {
    zone = 'TOO COMPLEX';
    color = '#f8ad9d';
  }

  return (
    <div className="w-full max-w-3xl mx-auto py-12 px-4">
      <div className="relative h-12 flex items-center">
        <div className="absolute w-full h-1 bg-border rounded-full overflow-hidden">
          <div className="w-1/3 h-full bg-blue/20 float-left" />
          <div className="w-1/3 h-full bg-gold/20 float-left" />
          <div className="w-1/3 h-full bg-red/20 float-left" />
        </div>
        <div className="absolute w-full flex justify-between text-[10px] text-muted -top-6 uppercase tracking-widest">
          <span>Rigid</span>
          <span>Fluid (Flow)</span>
          <span>Chaos</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={value}
          onChange={(e) => setValue(parseInt(e.target.value))}
          className="absolute w-full h-12 opacity-0 cursor-pointer z-20"
        />
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-bg border-2 rounded-full z-10 shadow-lg flex items-center justify-center"
          style={{ left: `calc(${value}% - 12px)`, borderColor: color }}
          animate={{ borderColor: color }}
        >
          <div className="w-1 h-1 rounded-full bg-text" />
        </motion.div>
      </div>
      <div className="mt-16 grid grid-cols-3 gap-8 text-center opacity-50 transition-opacity duration-300">
        <div
          className={`p-4 border border-border rounded transition-all ${
            zone === 'TOO SIMPLE' ? 'opacity-100 scale-105 border-blue' : 'opacity-40'
          }`}
        >
          <div className="text-2xl mb-2">😊</div>
          <h4 className="text-sm font-bold text-text mb-1">Notebook LM</h4>
          <p className="text-xs text-muted">
            "Input → Output"
            <br />
            Low Control
          </p>
        </div>
        <div
          className={`p-4 border border-border rounded transition-all ${
            zone === 'OPTIMAL' ? 'opacity-100 scale-110 border-gold bg-[#1a1510]' : 'opacity-40'
          }`}
        >
          <div className="text-2xl mb-2">⚡</div>
          <h4 className="text-sm font-bold text-gold mb-1">IFS Agent</h4>
          <p className="text-xs text-muted">
            Guided Workflow
            <br />
            The Golden Zone
          </p>
        </div>
        <div
          className={`p-4 border border-border rounded transition-all ${
            zone === 'TOO COMPLEX' ? 'opacity-100 scale-105 border-red' : 'opacity-40'
          }`}
        >
          <div className="text-2xl mb-2">😟</div>
          <h4 className="text-sm font-bold text-text mb-1">Raw API / Git</h4>
          <p className="text-xs text-muted">
            Engineering Heavy
            <br />
            Loss of Focus
          </p>
        </div>
      </div>
    </div>
  );
};

export const WaveVisual: React.FC<SlideProps> = () => {
  return (
    <div className="relative w-full h-[400px] flex items-end justify-center pb-12 overflow-hidden">
      <div
        className="absolute inset-0 z-0 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      ></div>

      <svg viewBox="0 0 800 300" className="w-full max-w-4xl h-full overflow-visible z-10">
        <defs>
          <linearGradient id="waveGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#f8ad9d" stopOpacity="0.5" />
            <stop offset="45%" stopColor="#d4a373" stopOpacity="1" />
            <stop offset="100%" stopColor="#666" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <motion.path
          d="M0,300 C200,300 300,300 350,200 C400,100 450,50 500,50 C600,50 700,150 800,150"
          fill="none"
          stroke="url(#waveGradient)"
          strokeWidth="4"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2, ease: 'easeOut' }}
        />
        <g className="opacity-60">
          <text x="150" y="250" fill="#f8ad9d" fontSize="12" fontFamily="IBM Plex Mono" textAnchor="middle">
            TOO EARLY
          </text>
          <text x="150" y="270" fill="#666" fontSize="10" fontFamily="IBM Plex Mono" textAnchor="middle">
            "Nobody understands"
          </text>
          <circle cx="150" cy="300" r="4" fill="#f8ad9d" />
        </g>
        <motion.g initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}>
          <line x1="425" y1="80" x2="425" y2="300" stroke="#d4a373" strokeWidth="1" strokeDasharray="4 4" />
          <circle cx="425" cy="80" r="6" fill="#0a0a0a" stroke="#d4a373" strokeWidth="2" />
          <text x="425" y="40" fill="#d4a373" fontSize="16" fontFamily="serif" fontStyle="italic" textAnchor="middle">
            The Golden Moment
          </text>
          <text x="425" y="60" fill="#e8e8e8" fontSize="10" fontFamily="IBM Plex Mono" textAnchor="middle">
            IFS + AI
          </text>
        </motion.g>
        <g className="opacity-40">
          <text x="650" y="100" fill="#666" fontSize="12" fontFamily="IBM Plex Mono" textAnchor="middle">
            COMMODITY
          </text>
          <text x="650" y="120" fill="#666" fontSize="10" fontFamily="IBM Plex Mono" textAnchor="middle">
            "Everyone has this"
          </text>
        </g>
      </svg>
    </div>
  );
};

export const StackVisual: React.FC<SlideProps> = () => {
  return (
    <div className="w-full max-w-lg mx-auto flex flex-col gap-4">
      <StackCard label="INPUT / RECORDING" title="Granola / Fathom" icon={<MessageSquare size={16} />} active={true} delay={0.2} />
      <div className="h-8 w-[1px] bg-border mx-auto relative overflow-hidden">
        <motion.div
          className="absolute top-0 left-0 w-full bg-gold h-full"
          initial={{ y: '-100%' }}
          animate={{ y: '100%' }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        />
      </div>
      <StackCard label="PROCESSING ENGINE" title="LLM (IFS Prompting)" icon={<Brain size={16} />} active={true} isGold delay={0.4} />
      <div className="h-8 w-[1px] bg-border mx-auto relative overflow-hidden">
        <motion.div
          className="absolute top-0 left-0 w-full bg-gold h-full"
          initial={{ y: '-100%' }}
          animate={{ y: '100%' }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear', delay: 0.75 }}
        />
      </div>
      <StackCard label="KNOWLEDGE BASE" title="NotebookLM / Obsidian" icon={<Layers size={16} />} active={true} delay={0.6} />
    </div>
  );
};

const StackCard = ({ label, title, icon, isGold, delay }: any) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay }}
    className={`p-4 border rounded bg-card relative overflow-hidden group ${
      isGold ? 'border-gold shadow-[0_0_20px_rgba(212,163,115,0.1)]' : 'border-border'
    }`}
  >
    {isGold && <div className="absolute top-0 left-0 w-1 h-full bg-gold" />}
    <div className="flex justify-between items-center mb-1">
      <span className="text-[9px] text-muted tracking-widest uppercase">{label}</span>
      <span className={`${isGold ? 'text-gold' : 'text-muted'}`}>{icon}</span>
    </div>
    <div className={`text-lg font-mono ${isGold ? 'text-text' : 'text-text/80'}`}>{title}</div>
  </motion.div>
);


